"use client"

import { memo, useCallback, useContext, useEffect, useMemo, useState } from "react"
import {
  Alert,
  alpha,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import dayjs from "dayjs"
import SearchIcon from "@mui/icons-material/Search"
import CloseIcon from "@mui/icons-material/Close"
import GavelIcon from "@mui/icons-material/Gavel"
import PaidIcon from "@mui/icons-material/Paid"
import PersonIcon from "@mui/icons-material/Person"
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth"
import LocationOnIcon from "@mui/icons-material/LocationOn"
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import PercentIcon from "@mui/icons-material/Percent"
import HelpOutlineIcon from "@mui/icons-material/HelpOutline"

import { ErpContext } from "../../context/ErpContext.js"

const ID_TIPO_TRANSACCION_RETENCION_ARCA = 12
/** Tipo entidad «Cliente» en el ERP (evita cargar getTipoEntidad en cada apertura). */
const TIPO_ENTIDAD_CLIENTE_IDS = [1]
const ENTITY_SEARCH_DEBOUNCE_MS = 450

const TIPOS_COMPROBANTE = [
  { value: "1", label: "01 — Factura A" },
  { value: "6", label: "06 — Factura B" },
  { value: "11", label: "11 — Factura C" },
  { value: "3", label: "03 — Factura de Servicios" },
  { value: "51", label: "51 — Factura M" },
]

const tooltipSx = { maxWidth: 320, typography: "body2", lineHeight: 1.45, p: 0.25 }

/** Icono ? con explicación; reduce carga vs re-renderizar tooltips en cada tecla si está memoizado por campo */
function FieldHelp({ children, ariaLabel = "Ayuda" }) {
  return (
    <Tooltip title={<Box sx={tooltipSx}>{children}</Box>} arrow placement="top" enterNextDelay={400}>
      <IconButton
        type="button"
        size="small"
        aria-label={ariaLabel}
        tabIndex={-1}
        sx={{ p: 0.25, ml: 0.25, color: "action.active", "&:hover": { color: "primary.main" } }}
        onClick={(e) => e.preventDefault()}
      >
        <HelpOutlineIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Tooltip>
  )
}

function labelWithHelp(text, helpNode, ariaLabel) {
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
      {text}
      <FieldHelp ariaLabel={ariaLabel}>{helpNode}</FieldHelp>
    </Box>
  )
}

const SectionHeader = memo(function SectionHeader({ icon, title, subtitle }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 2,
        py: 1,
        bgcolor: "#5b5b5f",
        borderRadius: "8px 8px 0 0",
      }}
    >
      {icon && <Box sx={{ color: "rgba(255,255,255,0.85)", display: "flex" }}>{icon}</Box>}
      <Box>
        <Typography variant="body2" fontWeight={700} sx={{ color: "white", letterSpacing: 0.5 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)" }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  )
})

const Section = memo(function Section({ children, icon, title, subtitle, sx = {} }) {
  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden", mb: 2, ...sx }}>
      <SectionHeader icon={icon} title={title} subtitle={subtitle} />
      <Box sx={{ p: 2 }}>{children}</Box>
    </Paper>
  )
})

export default function ModalNuevaRetencionArca({ open, onClose, onCreated }) {
  const {
    getUbicacion,
    ubicaciones,
    getEntidadesClientesTresLetras,
    entidadesClientesTresLetras,
    getRetencionesRegimenes,
    postRetencionSire,
  } = useContext(ErpContext)

  const [loading, setLoading] = useState(false)
  const [regimenes, setRegimenes] = useState([])
  const [cuitProveedor, setCuitProveedor] = useState("")
  const [importeTotal, setImporteTotal] = useState("")
  const [fechaFactura, setFechaFactura] = useState(() => dayjs())
  const [fechaRetencion, setFechaRetencion] = useState(() => dayjs())
  const [regimen, setRegimen] = useState(217)
  const [alicuotaOverride, setAlicuotaOverride] = useState("")
  const [tipoComprobante, setTipoComprobante] = useState("1")
  const [numeroComprobante, setNumeroComprobante] = useState("")
  const [selectedUbicacion, setSelectedUbicacion] = useState(null)
  const [selectedEntidad, setSelectedEntidad] = useState(null)
  const [entitySearch, setEntitySearch] = useState("")
  const [debouncedEntitySearch, setDebouncedEntitySearch] = useState("")
  const [entitySearchLoading, setEntitySearchLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")

  const ubicacionesPuntoVenta = useMemo(
    () => (ubicaciones || []).filter((u) => u.esDestino === false),
    [ubicaciones],
  )

  const listaEntidadesBusqueda = entidadesClientesTresLetras || []
  const unaSolaEntidad = listaEntidadesBusqueda.length === 1 && debouncedEntitySearch.length >= 3

  // Debounce búsqueda cliente — evita un GET por tecla (principal causa de lentitud)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedEntitySearch(entitySearch.trim()), ENTITY_SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [entitySearch])

  // Carga inicial en paralelo; sin getTipoEntidad (usamos tipo cliente fijo)
  useEffect(() => {
    if (!open) return
    let cancelled = false
    ;(async () => {
      try {
        await Promise.all([
          getUbicacion(),
          getRetencionesRegimenes().then(({ data }) => {
            if (cancelled || !data?.ok || !data.data?.length) return
            setRegimenes(data.data)
          }),
        ])
      } catch (e) {
        if (!cancelled) console.error(e)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, getUbicacion])

  // Búsqueda de entidades solo con texto debounced ≥ 3
  useEffect(() => {
    if (!open || debouncedEntitySearch.length < 3) {
      setEntitySearchLoading(false)
      return
    }
    let cancelled = false
    setEntitySearchLoading(true)
    getEntidadesClientesTresLetras(debouncedEntitySearch, TIPO_ENTIDAD_CLIENTE_IDS, false, false)
      .finally(() => {
        if (!cancelled) setEntitySearchLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, debouncedEntitySearch, getEntidadesClientesTresLetras])

  useEffect(() => {
    if (!open || debouncedEntitySearch.length < 3) return
    const list = entidadesClientesTresLetras || []
    if (list.length === 1) setSelectedEntidad(list[0])
  }, [open, debouncedEntitySearch, entidadesClientesTresLetras])

  useEffect(() => {
    if (open && regimenes.length && !regimenes.some((r) => r.regimen === regimen)) {
      setRegimen(regimenes[0].regimen)
    }
  }, [open, regimenes, regimen])

  useEffect(() => {
    if (!open) {
      setCuitProveedor("")
      setImporteTotal("")
      setFechaFactura(dayjs())
      setFechaRetencion(dayjs())
      setAlicuotaOverride("")
      setTipoComprobante("1")
      setNumeroComprobante("")
      setSelectedUbicacion(null)
      setSelectedEntidad(null)
      setEntitySearch("")
      setDebouncedEntitySearch("")
      setEntitySearchLoading(false)
      setErrorMessage("")
      setLoading(false)
    }
  }, [open])

  const regimenActual = useMemo(
    () => regimenes.find((x) => Number(x.regimen) === Number(regimen)),
    [regimenes, regimen],
  )

  const importeRetenidoEstimado = useMemo(() => {
    const base = Number(importeTotal)
    if (!Number.isFinite(base) || base <= 0) return null
    const aliRaw = alicuotaOverride === "" ? regimenActual?.alicuota : Number(alicuotaOverride)
    const ali = Number(aliRaw)
    if (!Number.isFinite(ali) || ali < 0) return null
    return Math.round(base * ali) / 100
  }, [importeTotal, regimenActual, alicuotaOverride])

  const handleEntityInputChange = useCallback((_, value, reason) => {
    if (reason === "input" || reason === "clear") setEntitySearch(value)
  }, [])

  const handleSubmit = async () => {
    setErrorMessage("")
    if (!cuitProveedor.trim()) {
      setErrorMessage("Ingresá el CUIT del proveedor.")
      return
    }
    const imp = Number(importeTotal)
    if (!Number.isFinite(imp) || imp <= 0) {
      setErrorMessage("El importe / base imponible debe ser un número mayor a cero.")
      return
    }

    setLoading(true)
    try {
      const payload = {
        idTipoTransaccion: ID_TIPO_TRANSACCION_RETENCION_ARCA,
        cuitProveedor: cuitProveedor.trim(),
        importeTotal: imp,
        fechaFactura: fechaFactura?.isValid() ? fechaFactura.format("YYYY-MM-DD") : null,
        fechaRetencion: fechaRetencion?.isValid() ? fechaRetencion.format("YYYY-MM-DD") : null,
        regimen: Number(regimen),
        alicuotaOverride: alicuotaOverride === "" ? null : Number(alicuotaOverride),
        tipoComprobante: Number(tipoComprobante) || 1,
        numeroComprobante: numeroComprobante?.trim() || null,
        idUbicacion: selectedUbicacion?.id ?? null,
        idEntidad: selectedEntidad?.id ?? null,
      }
      const { data } = await postRetencionSire(payload)
      if (!data.ok) throw new Error(data.message || "Error al guardar")
      onCreated?.(data.data)
      onClose?.()
    } catch (e) {
      const msg = e.response?.data?.message || e.message || "Error al guardar la retención."
      setErrorMessage(msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="md"
      fullWidth
      keepMounted={false}
      scroll="paper"
      PaperProps={{
        sx: { maxHeight: "92vh", display: "flex", flexDirection: "column", borderRadius: 2 },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "#3d3d40",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <GavelIcon sx={{ fontSize: 26, color: "rgb(244, 177, 14)" }} />
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: "white", lineHeight: 1.2 }}>
              Nueva retención ARCA (SIRE)
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
              Paso 1: datos en el ERP · Paso 2: Informar SIRE desde la tabla
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} disabled={loading} size="small" sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "white" } }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DialogContent sx={{ p: 2, overflowY: "auto" }}>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 2, py: 0.5 }}>
            <Typography variant="body2">
              <strong>Relación entre campos:</strong> el <strong>CUIT proveedor</strong> es a quien se le retiene. La{" "}
              <strong>base imponible</strong> y el <strong>régimen</strong> definen el importe retenido. Ese monto y las{" "}
              <strong>fechas</strong> + <strong>comprobante</strong> son lo que luego envía SIRE al informar.
            </Typography>
          </Alert>

          <Section
            icon={<PersonIcon fontSize="small" />}
            title="Quién interviene"
            subtitle="Proveedor retenido (obligatorio) · Cliente del ERP solo para trazabilidad interna"
          >
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={5}>
                <TextField
                  label={labelWithHelp(
                    "CUIT proveedor *",
                    <>
                      <strong>Proveedor retenido</strong> (sujeto de la retención ante AFIP). Debe ser un CUIT válido de
                      11 dígitos. No es el CUIT de tu empresa: ese se toma de parámetros globales (agente de retención)
                      al pulsar <em>Informar SIRE</em>.
                    </>,
                    "Ayuda CUIT proveedor",
                  )}
                  value={cuitProveedor}
                  onChange={(e) => setCuitProveedor(e.target.value)}
                  fullWidth
                  size="small"
                  required
                  placeholder="20-12345678-9"
                  helperText="Sujeto al que se aplica la retención"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={7}>
                <Autocomplete
                  size="small"
                  fullWidth
                  options={listaEntidadesBusqueda}
                  value={selectedEntidad}
                  onChange={(_, value) => setSelectedEntidad(value || null)}
                  inputValue={entitySearch}
                  onInputChange={handleEntityInputChange}
                  loading={entitySearchLoading}
                  filterOptions={(opts) => opts}
                  open={unaSolaEntidad ? false : undefined}
                  disableClearable={unaSolaEntidad}
                  getOptionLabel={(option) =>
                    `${option?.descripcion || ""} ${option?.apellido || ""}`.trim() +
                    (option?.dniCuitCuil ? ` — ${option.dniCuitCuil}` : "")
                  }
                  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  noOptionsText={
                    entitySearch.trim().length < 3 ? "Escribí al menos 3 letras (búsqueda pausada)" : "Sin resultados"
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={labelWithHelp(
                        "Cliente ERP (opcional)",
                        <>
                          Opcional: vinculá la transacción a un <strong>cliente cargado en el ERP</strong> para reportes
                          y trazabilidad. <strong>No reemplaza</strong> al CUIT proveedor ante AFIP. La búsqueda se
                          ejecuta solo unos segundos después de que dejás de escribir, para no saturar el servidor.
                        </>,
                        "Ayuda cliente ERP",
                      )}
                      placeholder="3+ letras, esperá un momento…"
                      helperText={entitySearchLoading ? "Buscando…" : "Solo uso interno; SIRE usa el CUIT proveedor"}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {entitySearchLoading ? <CircularProgress color="inherit" size={16} sx={{ mr: 1 }} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <PersonIcon fontSize="small" color="action" />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Section>

          <Section
            icon={<PaidIcon fontSize="small" />}
            title="Cálculo y fechas"
            subtitle="Base × alícuota del régimen = importe retenido (igual que validará el backend)"
          >
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label={labelWithHelp(
                    "Base imponible *",
                    <>
                      Monto sobre el que se aplica la alícuota del régimen (ej. total de la factura del proveedor sujeta
                      a retención). El sistema calcula <strong>importe retenido = base × alícuota / 100</strong> salvo
                      que uses override.
                    </>,
                    "Ayuda base imponible",
                  )}
                  type="number"
                  value={importeTotal}
                  onChange={(e) => setImporteTotal(e.target.value)}
                  fullWidth
                  size="small"
                  required
                  inputProps={{ min: 0, step: "0.01" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PaidIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={5}>
                <TextField
                  select
                  label={labelWithHelp(
                    "Régimen SIRE",
                    <>
                      Define el <strong>tipo de retención</strong> (p. ej. Ganancias, IVA) y su <strong>alícuota</strong>{" "}
                      por defecto. Debe coincidir con lo que corresponda al comprobante y normativa. El código de
                      impuesto que se envía a SIRE depende de este régimen.
                    </>,
                    "Ayuda régimen SIRE",
                  )}
                  value={regimen}
                  onChange={(e) => setRegimen(Number(e.target.value))}
                  fullWidth
                  size="small"
                  helperText={regimenActual ? `Alícuota del régimen: ${regimenActual.alicuota}%` : ""}
                  InputLabelProps={{ shrink: true }}
                >
                  {regimenes.map((r) => (
                    <MenuItem key={r.regimen} value={r.regimen}>
                      {r.regimen} — {r.descripcion} ({r.alicuota}%)
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label={labelWithHelp(
                    "Alícuota override",
                    <>
                      Si lo dejás <strong>vacío</strong>, se usa la alícuota del régimen elegido. Si completás un
                      valor, <strong>ese porcentaje</strong> reemplaza al del régimen solo para este cálculo (útil si
                      aplica una alícuota puntual distinta).
                    </>,
                    "Ayuda alícuota override",
                  )}
                  type="number"
                  value={alicuotaOverride}
                  onChange={(e) => setAlicuotaOverride(e.target.value)}
                  fullWidth
                  size="small"
                  helperText="Vacío = régimen"
                  inputProps={{ min: 0, step: "0.01" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PercentIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <DatePicker
                  label={labelWithHelp(
                    "Fecha comprobante",
                    <>
                      Fecha del <strong>comprobante original</strong> del proveedor (factura u otro tipo). SIRE la usa en
                      el certificado junto con tipo y número de comprobante.
                    </>,
                    "Ayuda fecha comprobante",
                  )}
                  value={fechaFactura}
                  onChange={(v) => setFechaFactura(v)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      InputLabelProps: { shrink: true },
                      InputProps: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarMonthIcon fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <DatePicker
                  label={labelWithHelp(
                    "Fecha retención (pago)",
                    <>
                      Fecha en que <strong>efectivamente se retuvo</strong> o se realizó el pago sujeto a retención.
                      Suele ser la fecha de pago al proveedor; va al certificado SIRE como fecha de retención.
                    </>,
                    "Ayuda fecha retención",
                  )}
                  value={fechaRetencion}
                  onChange={(v) => setFechaRetencion(v)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      InputLabelProps: { shrink: true },
                      InputProps: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarMonthIcon fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                      },
                    },
                  }}
                />
              </Grid>

              {importeRetenidoEstimado != null && (
                <Grid item xs={12} sm={6} md={4}>
                  <Box
                    sx={{
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 1,
                      borderRadius: 1.5,
                      bgcolor: alpha("#7b68ee", 0.08),
                      border: `1px solid ${alpha("#7b68ee", 0.25)}`,
                      gap: 1,
                    }}
                  >
                    <PaidIcon sx={{ color: "#4a3fa0", fontSize: 20 }} />
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Retenido estimado
                        </Typography>
                        <Typography variant="body1" fontWeight={700} sx={{ color: "#4a3fa0" }}>
                          $ {importeRetenidoEstimado.toLocaleString("es-AR", { minimumFractionDigits: 2 })}
                        </Typography>
                      </Box>
                      <FieldHelp ariaLabel="Ayuda retenido estimado">
                        Vista previa: <strong>base × alícuota</strong> (mismo criterio que el servidor al guardar). El
                        certificado oficial lo obtiene AFIP al <em>Informar SIRE</em>.
                      </FieldHelp>
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Section>

          <Section
            icon={<ReceiptLongIcon fontSize="small" />}
            title="Comprobante y sucursal"
            subtitle="Datos que acompañan el certificado · Ubicación opcional del ERP"
            sx={{ mb: 0 }}
          >
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={5}>
                <TextField
                  select
                  label={labelWithHelp(
                    "Tipo comprobante AFIP",
                    <>
                      Código AFIP del <strong>tipo de comprobante</strong> asociado a la operación retenida (factura A,
                      B, C, etc.). Debe ser coherente con la factura del proveedor que estás reteniendo.
                    </>,
                    "Ayuda tipo comprobante",
                  )}
                  value={tipoComprobante}
                  onChange={(e) => setTipoComprobante(e.target.value)}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                >
                  {TIPOS_COMPROBANTE.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={7}>
                <TextField
                  label={labelWithHelp(
                    "Número comprobante (opcional)",
                    <>
                      Número del comprobante del proveedor (punto de venta + número si aplica). Si no lo tenés, podés
                      dejarlo vacío según política interna; verificá requisitos de SIRE para tu caso.
                    </>,
                    "Ayuda número comprobante",
                  )}
                  value={numeroComprobante}
                  onChange={(e) => setNumeroComprobante(e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="Ej: 00001-00000001"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ReceiptLongIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  size="small"
                  options={ubicacionesPuntoVenta}
                  getOptionLabel={(o) => o?.descripcion || ""}
                  value={selectedUbicacion}
                  onChange={(_, v) => setSelectedUbicacion(v)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={labelWithHelp(
                        "Ubicación / punto de venta (opcional)",
                        <>
                          <strong>Solo ERP:</strong> asocia la transacción a una sucursal o punto de venta para listados
                          y reportes. <strong>No sustituye</strong> el punto de venta fiscal de AFIP en el WSFE; en
                          SIRE el agente es tu CUIT de empresa.
                        </>,
                        "Ayuda ubicación",
                      )}
                      placeholder="Opcional"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <LocationOnIcon fontSize="small" color="action" />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 1.5 }} />

            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
              <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.secondary", mt: 0.1 }} />
              <Typography variant="caption" color="text.secondary">
                Al <strong>Guardar</strong> se crea la transacción tipo <strong>Retención ARCA ({ID_TIPO_TRANSACCION_RETENCION_ARCA})</strong> en
                la base. El envío a AFIP ocurre con <strong>Informar SIRE</strong> en la tabla (requiere certificado y CUIT
                agente en parámetros globales).
              </Typography>
            </Box>
          </Section>
        </DialogContent>
      </LocalizationProvider>

      <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: "1px solid #e0e0e0", bgcolor: "#fafafa", gap: 1 }}>
        <Button onClick={onClose} disabled={loading} sx={{ textTransform: "none", color: "text.secondary" }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <GavelIcon />}
          sx={{
            textTransform: "none",
            minWidth: 160,
            bgcolor: "#5b5b5f",
            "&:hover": { bgcolor: alpha("rgb(244, 177, 14)", 0.9), color: "#333" },
          }}
        >
          {loading ? "Guardando..." : "Guardar retención"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
